import { useCallback, useEffect, useState } from 'react';
import escalationsService from '../api/services/escalations';
import pendingApprovalsService from '../api/services/pendingApprovals';
import socket from '../api/socket';

export interface AdminQueueCounts {
  /** Escalations still on OPEN or IN_PROGRESS — the operator queue at /admin/escalations. */
  openEscalations: number;
  /** Approvals still on PENDING — worked from the task inbox at /admin. */
  pendingApprovals: number;
}

const ZERO: AdminQueueCounts = { openEscalations: 0, pendingApprovals: 0 };

/**
 * How many items are waiting in the two admin-only queues, for the PWA app
 * badge (#310). A count-only request ($limit: 1) is enough — both services
 * always return `total` regardless of how many rows come back.
 *
 * No polling: each count is fetched once, then kept live over the same socket
 * events the queues themselves listen for (`Escalations.tsx`, the task inbox's
 * `useApprovalQueue`), so this never opens a second connection or timer.
 */
export default function useAdminQueueCounts(
  enabled: boolean
): AdminQueueCounts {
  const [counts, setCounts] = useState<AdminQueueCounts>(ZERO);

  const loadEscalations = useCallback(async () => {
    try {
      const result = await escalationsService.find({
        status: ['OPEN', 'IN_PROGRESS'],
        $limit: 1,
      });
      setCounts((current) => ({ ...current, openEscalations: result.total }));
    } catch (error) {
      // A badge is not worth surfacing an error over; leave the last count.
      console.error('Failed to load the open escalations count', error);
    }
  }, []);

  const loadApprovals = useCallback(async () => {
    try {
      const result = await pendingApprovalsService.find({
        status: 'PENDING',
        $limit: 1,
      });
      setCounts((current) => ({ ...current, pendingApprovals: result.total }));
    } catch (error) {
      // Same here — a stale badge beats an error toast nobody asked to see.
      console.error('Failed to load the pending approvals count', error);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setCounts(ZERO);
      return undefined;
    }

    loadEscalations();
    loadApprovals();

    socket.on('escalations created', loadEscalations);
    socket.on('escalations patched', loadEscalations);
    socket.on('agent-coordinator/approvals created', loadApprovals);
    socket.on('agent-coordinator/approvals patched', loadApprovals);

    return () => {
      socket.off('escalations created', loadEscalations);
      socket.off('escalations patched', loadEscalations);
      socket.off('agent-coordinator/approvals created', loadApprovals);
      socket.off('agent-coordinator/approvals patched', loadApprovals);
    };
  }, [enabled, loadEscalations, loadApprovals]);

  return counts;
}
